import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import {
  Ban,
  CalendarPlus,
  Check,
  ChevronDown,
  CircleCheck,
  FilePenLine,
  PackagePlus,
  Pencil,
  Send,
  Trash2,
  Undo2,
} from 'lucide-react';

import { approvePurchaseOrder } from '#/api/srm/purchaseOrder/approve';
import { cancelDraftPurchaseOrder } from '#/api/srm/purchaseOrder/cancelDraft';
import { closePurchaseOrder } from '#/api/srm/purchaseOrder/close';
import { fetchPurchaseOrderStockReceiptList } from '#/api/srm/purchaseOrder/fetchStockReceiptList';
import { rejectPurchaseOrder } from '#/api/srm/purchaseOrder/reject';
import { submitPurchaseOrder } from '#/api/srm/purchaseOrder/submit';
import { terminatePurchaseOrder } from '#/api/srm/purchaseOrder/terminate';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/modules/commonComponents/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import {
  pickAvailableWorkflowExposedIds,
  resolveLgDesktopLayout,
  resolveMdDesktopLayout,
  resolveMobileLayout,
} from '#/modules/imsMgmt/receipts/shared/documentCommandBarLayout';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { useCan } from '#/modules/infra/auth/useCan';
import {
  getPurchaseOrderApiErrorMessage,
  isPurchaseOrderVersionConflict,
} from '#/modules/srmMgmt/purchaseOrder/helper/apiError';
import {
  canAmendPurchaseOrder,
  canApprovePurchaseOrder,
  canCancelDraftPurchaseOrder,
  canClosePurchaseOrder,
  canShowClosePurchaseOrderAction,
  canCreateArrivalSchedule,
  canCreateStockReceiptFromPurchaseOrder,
  canEditPurchaseOrderDraft,
  canRejectPurchaseOrder,
  canSubmitPurchaseOrder,
  canTerminatePurchaseOrder,
} from '#/modules/srmMgmt/purchaseOrder/helper/purchaseOrderStatus';
import {
  PURCHASE_AMENDMENT_STATUS,
  findOpenPurchaseAmendment,
} from '#/modules/srmMgmt/purchaseOrder/helper/purchaseAmendmentStatus';
import { toast } from '#/utils/toast';
import {
  purchaseOrderDetailQueryKey,
  purchaseOrderListQueryKeyPrefix,
  purchaseOrderStockReceiptListQueryKey,
} from '#/utils/queryKeys';

const WORKFLOW_EXPOSED_ORDER = [
  'submit',
  'approve',
  'reject',
  'close',
  'terminate',
  'cancelDraft',
];

const ACTION_GROUPS = [
  { key: 'content', label: '單據內容', ids: ['edit', 'createAmendment'] },
  {
    key: 'fulfillment',
    label: '履約',
    ids: ['createArrivalSchedule', 'createStockReceipt'],
  },
  {
    key: 'status',
    label: '單據狀態',
    ids: ['submit', 'approve', 'reject', 'close', 'terminate', 'cancelDraft'],
  },
];

const ACTION_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  to: PropTypes.string,
  onSelect: PropTypes.func,
  destructive: PropTypes.bool,
  disabled: PropTypes.bool,
  available: PropTypes.bool,
  showExposedIcon: PropTypes.bool,
  exposedVariant: PropTypes.string,
  exposedClassName: PropTypes.string,
  tooltip: PropTypes.string,
});

function getExposedActionIds(actionMap) {
  return pickAvailableWorkflowExposedIds(actionMap, WORKFLOW_EXPOSED_ORDER);
}

function runAfterMenuClose(callback) {
  window.setTimeout(() => {
    callback?.();
  }, 0);
}

function wrapWithTooltip(tooltip, node) {
  if (!tooltip) {
    return node;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {node}
      </TooltipTrigger>
      <TooltipContent side="bottom">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

function PurchaseOrderExposedActionButton({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);

  const button = action.to && !disabled ? (
    <Button
      asChild
      variant={action.exposedVariant || 'default'}
      className={cn(action.exposedClassName)}
    >
      <Link to={action.to} aria-label={action.label}>
        {action.showExposedIcon && Icon ? (
          <Icon className="size-3.5" aria-hidden />
        ) : null}
        {action.label}
      </Link>
    </Button>
  ) : (
    <Button
      type="button"
      variant={action.destructive ? 'destructive' : (action.exposedVariant || 'default')}
      className={cn(
        action.destructive && DESTRUCTIVE_SOLID_BUTTON_CLASS,
        action.exposedClassName,
      )}
      disabled={disabled}
      onClick={() => action.onSelect?.()}
    >
      {action.showExposedIcon && Icon ? (
        <Icon className="size-3.5" aria-hidden />
      ) : null}
      {action.label}
    </Button>
  );

  return wrapWithTooltip(action.tooltip, button);
}

PurchaseOrderExposedActionButton.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function PurchaseOrderMenuItem({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);
  const iconNode = Icon ? <Icon className="size-4" aria-hidden /> : null;

  const item = action.to && !disabled ? (
    <DropdownMenuItem
      asChild
      variant={action.destructive ? 'destructive' : 'default'}
    >
      <Link to={action.to}>
        {iconNode}
        {action.label}
      </Link>
    </DropdownMenuItem>
  ) : (
    <DropdownMenuItem
      variant={action.destructive ? 'destructive' : 'default'}
      disabled={disabled}
      onSelect={() => {
        if (disabled) {
          return;
        }
        runAfterMenuClose(action.onSelect);
      }}
    >
      {iconNode}
      {action.label}
    </DropdownMenuItem>
  );

  return wrapWithTooltip(action.tooltip, item);
}

PurchaseOrderMenuItem.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function PurchaseOrderActionsMenu({
  triggerLabel,
  triggerAriaLabel,
  groups,
  isPending,
}) {
  if (!groups.length) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={isPending}
          aria-label={triggerAriaLabel || triggerLabel}
        >
          {triggerLabel}
          <ChevronDown className="size-3.5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={cn('w-auto min-w-44')}>
        {groups.map((group, index) => (
          <Fragment key={group.key}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
            {group.items.map((action) => (
              <PurchaseOrderMenuItem
                key={action.id}
                action={action}
                isPending={isPending}
              />
            ))}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

PurchaseOrderActionsMenu.propTypes = {
  triggerLabel: PropTypes.string.isRequired,
  triggerAriaLabel: PropTypes.string,
  groups: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(ACTION_SHAPE).isRequired,
  })).isRequired,
  isPending: PropTypes.bool,
};

const PurchaseOrderCommandBar = ({ detail, onRefetch }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const canCreate = useCan(PERM.PURCHASE_ORDER_CREATE);
  const canUpdate = useCan(PERM.PURCHASE_ORDER_UPDATE);
  const canReview = useCan(PERM.PURCHASE_ORDER_REVIEW);
  const canCreateStockReceipt = useCan(PERM.STOCK_RECEIPT_CREATE);

  const id = detail?.id;
  const updatedAt = detail?.record?.updatedAt ?? detail?.updatedAt;
  const status = detail?.status;
  // 同一採購單同時最多一張在途異動；在途期間不可再建或結案
  const openAmendment = findOpenPurchaseAmendment(detail?.amendments);
  const openAmendmentIsDraft = openAmendment?.status === PURCHASE_AMENDMENT_STATUS.DRAFT;

  const stockReceiptListQuery = useQuery({
    queryKey: purchaseOrderStockReceiptListQueryKey(id, {}),
    queryFn: () => fetchPurchaseOrderStockReceiptList(id, {}),
    enabled: Boolean(id) && canReview && canClosePurchaseOrder(status),
  });

  const stockReceiptCount = stockReceiptListQuery.data?.data?.length ?? 0;

  async function invalidateAll() {
    await queryClient.invalidateQueries({
      queryKey: purchaseOrderListQueryKeyPrefix(),
    });
    if (id) {
      await queryClient.invalidateQueries({
        queryKey: purchaseOrderDetailQueryKey(id),
      });
    }
    await onRefetch?.();
  }

  const commandMutation = useMutation({
    mutationFn: async ({ action, payload }) => {
      if (action === 'submit') {
        return submitPurchaseOrder(id, payload);
      }
      if (action === 'approve') {
        return approvePurchaseOrder(id, payload);
      }
      if (action === 'reject') {
        return rejectPurchaseOrder(id, payload);
      }
      if (action === 'close') {
        return closePurchaseOrder(id, payload);
      }
      if (action === 'terminate') {
        return terminatePurchaseOrder(id, payload);
      }
      if (action === 'cancelDraft') {
        return cancelDraftPurchaseOrder(id, payload);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'reject') {
        setRejectDialogOpen(false);
      }
      if (variables.action === 'terminate') {
        setTerminateDialogOpen(false);
      }
      if (variables.action === 'cancelDraft') {
        await queryClient.invalidateQueries({
          queryKey: purchaseOrderListQueryKeyPrefix(),
        });
        navigate(ROUTES.purchaseOrder);
        return;
      }
      await invalidateAll();
    },
    onError: (error) => {
      toast.error(getPurchaseOrderApiErrorMessage(error));
      if (isPurchaseOrderVersionConflict(error)) {
        onRefetch?.();
      }
    },
  });

  const isPending = commandMutation.isPending;

  async function handleSubmit() {
    const ok = await confirm({
      title: '提交物料採購單',
      message: '提交後進入待核准，確定提交？',
      confirmText: '提交',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'submit', payload: { updatedAt } });
  }

  async function handleApprove() {
    const ok = await confirm({
      title: '核准物料採購單',
      message: '核准後可進行入庫相關流程，確定核准？',
      confirmText: '核准',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'approve', payload: { updatedAt } });
  }

  function handleRejectConfirm(rejectReason) {
    commandMutation.mutate({
      action: 'reject',
      payload: { updatedAt, rejectReason },
    });
  }

  async function handleClose() {
    const ok = await confirm({
      title: '結案物料採購單',
      message: '結案後不可再入庫，確定結案？',
      confirmText: '結案',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'close', payload: { updatedAt } });
  }

  function handleTerminateConfirm(terminateReason) {
    commandMutation.mutate({
      action: 'terminate',
      payload: { updatedAt, terminateReason },
    });
  }

  async function handleCancelDraft() {
    const ok = await confirm({
      title: '刪除草稿',
      message: '草稿將永久刪除，無法復原，確定刪除？',
      confirmText: '刪除草稿',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'cancelDraft', payload: { updatedAt } });
  }

  const actionMap = {
    edit: {
      id: 'edit',
      label: '編輯',
      icon: Pencil,
      to: id ? ROUTES.purchaseOrderEdit(id) : undefined,
      available: canUpdate && canEditPurchaseOrderDraft(status),
      showExposedIcon: true,
      exposedVariant: 'default',
      exposedClassName: 'document-detail-edit-action',
    },
    createAmendment: {
      id: 'createAmendment',
      label: openAmendmentIsDraft ? '編輯異動草稿' : '新增異動',
      icon: FilePenLine,
      to: id
        ? (openAmendmentIsDraft
          ? ROUTES.purchaseOrderAmendmentEdit(id, openAmendment.id)
          : ROUTES.purchaseOrderAmendmentCreate(id))
        : undefined,
      available: canUpdate && canAmendPurchaseOrder(status)
        && (!openAmendment || openAmendmentIsDraft),
      showExposedIcon: true,
      exposedVariant: 'outline',
    },
    createArrivalSchedule: {
      id: 'createArrivalSchedule',
      label: '新建排程',
      icon: CalendarPlus,
      to: id ? ROUTES.purchaseOrderArrivalScheduleCreate(id) : undefined,
      available: canCreate && canCreateArrivalSchedule(status),
      showExposedIcon: true,
      exposedVariant: 'outline',
    },
    createStockReceipt: {
      id: 'createStockReceipt',
      label: '新建入庫',
      icon: PackagePlus,
      to: id ? ROUTES.purchaseOrderInboundCreate(id) : undefined,
      available: canCreateStockReceipt && canCreateStockReceiptFromPurchaseOrder(status),
      showExposedIcon: true,
      exposedVariant: 'outline',
    },
    submit: {
      id: 'submit',
      label: '提交',
      icon: Send,
      available: canUpdate && canSubmitPurchaseOrder(status),
      onSelect: handleSubmit,
    },
    approve: {
      id: 'approve',
      label: '核准',
      icon: Check,
      available: canReview && canApprovePurchaseOrder(status),
      onSelect: handleApprove,
    },
    reject: {
      id: 'reject',
      label: '退回',
      icon: Undo2,
      available: canReview && canRejectPurchaseOrder(status),
      destructive: true,
      onSelect: () => setRejectDialogOpen(true),
    },
    close: {
      id: 'close',
      label: '結案',
      icon: CircleCheck,
      available: canReview
        && !openAmendment
        && stockReceiptListQuery.isSuccess
        && canShowClosePurchaseOrderAction(status, stockReceiptCount),
      onSelect: handleClose,
    },
    terminate: {
      id: 'terminate',
      label: '中止',
      icon: Ban,
      // 仍有未交量才可中止；全數入庫請結案（後端 409 PURCHASE_ORDER_TERMINATE_NOT_ALLOWED）
      available: canReview
        && !openAmendment
        && canTerminatePurchaseOrder(status, detail?.lines),
      destructive: true,
      onSelect: () => setTerminateDialogOpen(true),
    },
    cancelDraft: {
      id: 'cancelDraft',
      label: '刪除草稿',
      icon: Trash2,
      // be doc：DELETE 需 srm.purchase_order.update
      available: canUpdate && canCancelDraftPurchaseOrder(status),
      destructive: true,
      onSelect: handleCancelDraft,
    },
  };

  const mobileLayout = resolveMobileLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
  });
  const mdLayout = resolveMdDesktopLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
    getExposedActionIds,
  });
  const lgLayout = resolveLgDesktopLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
    getExposedActionIds,
  });

  function renderLayoutRow(
    layout,
    { triggerLabel = '更多', triggerAriaLabel = '更多操作' } = {},
  ) {
    return (
      <>
        {layout.flatActions.map((action) => (
          <PurchaseOrderExposedActionButton
            key={action.id}
            action={action}
            isPending={isPending}
          />
        ))}
        {layout.showMoreMenu ? (
          <PurchaseOrderActionsMenu
            triggerLabel={triggerLabel}
            triggerAriaLabel={triggerAriaLabel}
            groups={layout.menuGroups}
            isPending={isPending}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      {ConfirmDialogHost}
      <DocumentRejectReasonDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        title="退回物料採購單"
        description="退回後將恢復為草稿，確定退回？"
        confirmText="退回"
        isPending={isPending}
        onConfirm={handleRejectConfirm}
      />
      <DocumentRejectReasonDialog
        open={terminateDialogOpen}
        onOpenChange={setTerminateDialogOpen}
        title="中止物料採購單"
        description="停止剩餘未交數量，中止後不可再入庫或異動，確定中止？"
        confirmText="中止"
        reasonLabel="中止原因"
        emptyErrorMessage="請填寫中止原因"
        isPending={isPending}
        onConfirm={handleTerminateConfirm}
      />

      <div className={cn('flex items-center justify-end gap-2')}>
        <div className={cn('flex md:hidden', 'items-center gap-2')}>
          {renderLayoutRow(mobileLayout, {
            triggerLabel: '操作',
            triggerAriaLabel: '單據操作',
          })}
        </div>
        <div className={cn('hidden md:flex lg:hidden', 'items-center gap-2')}>
          {renderLayoutRow(mdLayout)}
        </div>
        <div className={cn('hidden lg:flex', 'items-center gap-2')}>
          {renderLayoutRow(lgLayout)}
        </div>
      </div>
    </>
  );
};

PurchaseOrderCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    updatedAt: PropTypes.string,
    lines: PropTypes.arrayOf(PropTypes.object),
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default PurchaseOrderCommandBar;
